/** Mirrors `@shared/contracts/models` in the Libra monorepo. Keep the two in step. */

export type ModelApi =
  | "openai-completions"
  | "openai-responses"
  | "anthropic-messages"
  | "google-generative-ai"
  | "bedrock-converse"
  | "openai-images"
  | "openrouter-images"
  | "vertex-images";

export const MODEL_APIS: ModelApi[] = [
  "openai-completions",
  "openai-responses",
  "anthropic-messages",
  "google-generative-ai",
  "bedrock-converse",
  "openai-images",
  "openrouter-images",
  "vertex-images",
];

export const IMAGE_APIS = new Set<ModelApi>([
  "openai-images",
  "openrouter-images",
  "vertex-images",
]);

export type ModelKind = "chat" | "image";
export type ModelState = "active" | "hidden" | "disabled";
export type Modality = "text" | "image" | "pdf";
export type ModelRoleName = "chat_default" | "title" | "compaction";

export const MODEL_STATES: ModelState[] = ["active", "hidden", "disabled"];
export const MODALITIES: Modality[] = ["text", "image", "pdf"];

export const kindForApi = (api: ModelApi): ModelKind =>
  IMAGE_APIS.has(api) ? "image" : "chat";

export interface ChatCost {
  input: number;
  output: number;
  cacheRead?: number;
  cacheWrite?: number;
}
export interface ImageCost {
  perImage: Array<{
    quality?: string;
    size?: string;
    aspectRatio?: string;
    usd: number;
  }>;
}
export type ModelCost = ChatCost | ImageCost;

/** OpenRouter provider routing, camelCase. The server turns it into OpenRouter's wire shape. */
export interface OpenRouterRouting {
  order?: string[];
  only?: string[];
  ignore?: string[];
  quantizations?: string[];
  sort?: "price" | "latency" | "throughput";
  dataCollection?: "allow" | "deny";
  requireParameters?: boolean;
  allowFallbacks?: boolean;
  zdr?: boolean;
}

export interface ModelSettings {
  temperature?: number;
  topP?: number;
  maxOutputTokens?: number;
  effort?: "low" | "medium" | "high";
  providerOptions?: { openrouter?: OpenRouterRouting } & Record<
    string,
    unknown
  >;
}

/** One upstream that serves a model on OpenRouter. Prices are USD per one million tokens. */
export interface VendorEndpoint {
  provider: string;
  tag: string | null;
  quantization: string | null;
  contextWindow: number | null;
  maxOutputTokens: number | null;
  costInput: number | null;
  costOutput: number | null;
  uptime: number | null;
  latencyMs: number | null;
  throughput: number | null;
  healthy: boolean;
}

export interface VendorSnapshot {
  name?: string;
  description?: string;
  reasoning?: boolean;
  input?: Modality[];
  contextWindow?: number;
  maxOutputTokens?: number;
  cost?: ModelCost;
  fetchedAt: string;
}

export interface ModelProvider {
  id: string;
  slug: string;
  name: string;
  baseUrl: string | null;
  apiKeyEnv: string | null;
  headers: Record<string, string> | null;
  isActive: boolean;
  /** Whether the named env key holds a value on the server. Never the value. */
  keyConfigured: boolean;
}

export interface ModelTestResult {
  reply: string;
  latencyMs: number;
  inputTokens: number | null;
  outputTokens: number | null;
}

export interface ModelProviderWrite {
  slug: string;
  name: string;
  baseUrl?: string | null;
  apiKeyEnv?: string | null;
  headers?: Record<string, string> | null;
  isActive?: boolean;
}

export interface AdminModel {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  kind: ModelKind;
  api: ModelApi;
  provider: ModelProvider;
  modelName: string;
  reasoning: boolean;
  input: Modality[];
  contextWindow: number | null;
  maxOutputTokens: number | null;
  cost: ModelCost | null;
  settings: ModelSettings | null;
  imageOptions: Record<string, unknown> | null;
  state: ModelState;
  displayOrder: number;
  vendor: VendorSnapshot | null;
  syncedAt: string | null;
  roles: ModelRoleName[];
}

export interface ModelWrite {
  slug: string;
  providerId: string;
  modelName: string;
  api: ModelApi;
  kind: ModelKind;
  name: string;
  description?: string | null;
  reasoning?: boolean | null;
  input?: Modality[];
  contextWindow?: number | null;
  maxOutputTokens?: number | null;
  cost?: ModelCost | null;
  settings?: ModelSettings | null;
  state?: ModelState;
  displayOrder?: number;
}
export type ModelPatch = Partial<Omit<ModelWrite, "slug">>;

export interface ModelRoleBinding {
  role: ModelRoleName;
  modelId: string | null;
}

export interface VendorCatalogEntry {
  modelName: string;
  name: string;
  kind: ModelKind;
  contextWindow: number | null;
}
